import React, { useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import { useDailyTipperDataQuery, useDtWeeklyProgressQuery } from '@/graphql';
import type { DailyTipperDataQuery, DtWeeklyProgressQuery } from '@/graphql';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAuth } from '@/contexts/auth-context';

interface IDailyTipperDataContext {
  dtData: DailyTipperDataQuery | undefined;
  dtWeek: DtWeeklyProgressQuery | undefined;
  loadingDtData: boolean;
  loadingDtWeek: boolean;
  // `dailyTipperData` returns ERROR_8 (must join a network classroom) as a GraphQL error.
  // Exposed from this single cache-and-network query so consumers branch on a live error
  // instead of their own cache-first reads (errors aren't cached → a cache hit hides them).
  dtDataError: ApolloError | undefined;
  // Forces a refresh of the shared `dailyTipperData` query (e.g. after answering); every
  // consumer reading `dtData` updates from the one cache entry.
  refetchDtData: ReturnType<typeof useDailyTipperDataQuery>['refetch'];
}

const DailyTipperDataContext = React.createContext<IDailyTipperDataContext | undefined>(undefined);

export const DailyTipperDataProvider: React.FC = () => {
  const { token } = useAuth();
  const { dailyTipperEnabled } = useFeatureFlags();
  const skip = !token || !dailyTipperEnabled;

  const {
    data: dtData,
    loading: loadingDtData,
    error: dtDataError,
    refetch: refetchDtData,
  } = useDailyTipperDataQuery({
    fetchPolicy: 'cache-and-network',
    skip,
    context: { errorHandling: 'silent' },
  });
  const { data: dtWeek, loading: loadingDtWeek } = useDtWeeklyProgressQuery({
    fetchPolicy: 'cache-and-network',
    skip,
  });

  return (
    <DailyTipperDataContext.Provider
      value={{ dtData, dtWeek, loadingDtData, loadingDtWeek, dtDataError, refetchDtData }}
    >
      <Outlet />
    </DailyTipperDataContext.Provider>
  );
};

export const useDailyTipperData = () => {
  const state = useContext(DailyTipperDataContext);
  if (!state) throw new Error('DailyTipperDataProvider not found');
  return state;
};
